/**
 * Durable, per-company marker of how far the user got in the onboarding wizard.
 *
 * The wizard step lives only in in-memory Vue state (`useOnboardingWizard`), so a
 * page reload used to drop the user back on step 1 (the website/URL screen) even
 * though the scrape was already running server-side. We persist a lightweight
 * marker here and restore it on mount so a refresh keeps the user where they were.
 *
 * Backend data (company record + genaisis session) remains the source of truth for
 * the *values*; this only remembers the *position* in the flow.
 */
export type OnboardingProgressMarker = {
  step: number
  slug: string
}

const storageKey = (companyId: string) => `onboarding:progress:${companyId}`

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Access can throw in privacy modes / sandboxed iframes.
    return null
  }
}

export function readOnboardingProgress(
  companyId: string | null | undefined
): OnboardingProgressMarker | null {
  if (!companyId) return null
  const store = safeLocalStorage()
  if (!store) return null
  try {
    const raw = store.getItem(storageKey(companyId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<OnboardingProgressMarker>
    if (typeof parsed?.step !== 'number' || !Number.isFinite(parsed.step)) return null
    return { step: parsed.step, slug: typeof parsed.slug === 'string' ? parsed.slug : '' }
  } catch {
    return null
  }
}

export function writeOnboardingProgress(
  companyId: string | null | undefined,
  marker: OnboardingProgressMarker
): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    store.setItem(storageKey(companyId), JSON.stringify(marker))
  } catch {
    // Quota / disabled storage — best-effort only.
  }
}

/**
 * Per-company record of which one-off Heap funnel milestones (e.g.
 * "chat_reached") already fired. Component-local booleans would reset on
 * every mount, so a page reload while resuming onboarding (restored via the
 * marker above) would re-fire and double-count a milestone already reached
 * in an earlier session.
 */
const milestonesKey = (companyId: string) => `onboarding:heap-milestones:${companyId}`

export function clearOnboardingProgress(companyId: string | null | undefined): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    store.removeItem(storageKey(companyId))
    store.removeItem(milestonesKey(companyId))
  } catch {
    // no-op
  }
}

export function readTrackedOnboardingMilestones(companyId: string | null | undefined): Set<string> {
  if (!companyId) return new Set()
  const store = safeLocalStorage()
  if (!store) return new Set()
  try {
    const raw = store.getItem(milestonesKey(companyId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

export function markOnboardingMilestoneTracked(
  companyId: string | null | undefined,
  milestone: string
): void {
  if (!companyId) return
  const store = safeLocalStorage()
  if (!store) return
  try {
    const current = readTrackedOnboardingMilestones(companyId)
    current.add(milestone)
    store.setItem(milestonesKey(companyId), JSON.stringify([...current]))
  } catch {
    // Quota / disabled storage — best-effort only.
  }
}
